import { Autocomplete, Button, FormControl, Grid, InputAdornment, Menu, MenuItem, Stack, TextField } from "@mui/material"
import datepicker from '../../../assets/Images/Profile/datepicker-icon.svg'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import { useContext, useEffect, useState, type SyntheticEvent } from "react"
import moment from "moment"
import profileStyle from "../../../Styles/ComponentStyles/ProfileStyle/Profile.module.scss"
import { UserContext, UserContextPayload } from "../../../Context/UserDetailContext"
import GlobalButton from "../../../Utils/GlobalUtilsComponents/GlobalButton"
import { DataGrid, GridColDef, GridToolbar, GridToolbarContainer, GridValueGetterParams } from "@mui/x-data-grid"
import CustomPagination from "../../../GlobalComponent/CustomPagination/CustomPagination"
import globalStyle from "../../../Styles/ComponentStyles/global.module.scss"
import { useNavigate, useOutletContext } from "react-router-dom"
import { ProfileContext, ProfileContextPayload } from "../../../Context/ProfileDetailsContext"
import agent from "../../../service/api"
import { useSnackbar } from "../../../GlobalComponent/GlobalSnackbar/GlobalSnackbar"
import MoreVertIcon from '@mui/icons-material/MoreVert'
import GlobalPopUp from "../../../GlobalComponent/GlobalPopup/GlobalPopup"
import GlobalDeletePopup from "../../../GlobalComponent/GlobalDeletePopup/GlobalDeletePopup"
import { FilterPayload } from "../../../Models/axiosModel"
import AssignProfileToAgentPopup from "../AssignProfileToAgentPopup"
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import SaveIcon from '@mui/icons-material/Save';

interface ProfileListProps {
    profileType: 'iul' | 'annuity' | 'all'
}

interface AgentOption {
    user_id: number
    user_firstname: string
    user_lastname: string
    user_role_type?: string
}

interface ProfileOutletContext {
    agentOptions: AgentOption[]
    selectedAgent: AgentOption | null
    handleAgentChange: (event: SyntheticEvent<Element, Event>, newValue: AgentOption | null) => void
}

const PROFILE_TYPE_LABELS: Record<number, string> = {
    1: "IUL",
    2: "New Annuity - Roth Conversion",
    3: "New Annuity - Income",
    4: "New Annuity - Accumulation",
}

const getProfileTypeLabel = (profileTypeId: number): string | null =>
    PROFILE_TYPE_LABELS[profileTypeId] ?? null

const ProfileListNoRowsOverlay = () => (
    <Stack height="100%" alignItems="center" justifyContent="center" color="#0F172B">
        No Data Found
    </Stack>
)

const ProfileListCustomToolbar = () => (
    <GridToolbarContainer
        sx={{
            margin: '5px 0px 25px',
            float: 'right',
            '& .MuiButtonBase-root': {
                color: '#ffffff !important',
            },
        }}
    >
        <GridToolbar />
    </GridToolbarContainer>
)

interface ProfileColumn {
    field: string
    headerName: string
    type?: string
    align: string
    headerAlign: string
    width: number
    sortable?: boolean
    disableColumnMenu?: boolean
    valueGetter?: (params: GridValueGetterParams) => any
    renderCell?: (params: any) => JSX.Element
}

/** Sample rows: API may send `sample_profile` or `sample_profile_flag`. */
const isSampleProfileRow = (row: any) => Boolean(row?.sample_profile ?? row?.sample_profile_flag)

const ProfileList = ({ profileType }: ProfileListProps) => {
    const { userDetail, setFlagLoader } = useContext(UserContext) as UserContextPayload
    const {
        iulProfileDetails,
        setIulProfileDetails,
        annuityProfileDetails,
        setAnnuityProfileDetails,
        deleteUserProfileAPI,
        assignAgentAPI
    } = useContext(ProfileContext) as ProfileContextPayload

    // Get agent data from parent via outlet context
    const { agentOptions, selectedAgent, handleAgentChange } = useOutletContext<ProfileOutletContext>()

    const userRole = userDetail?.user_data?.user_role_type.toLowerCase()
    const navigate = useNavigate()
    const { showSnackbar } = useSnackbar()

    const [profileFilterDetails, setProfileFilterDetails] = useState<FilterPayload>({
        agent_id: 0,
        date_filter_flag: false,
        from_timestamp: moment().subtract(60, "days").format("MM/DD/YYYY"),
        to_timestamp: moment().format("MM/DD/YYYY"),
    })
    const [startDate, setStartDate] = useState(
        profileFilterDetails.from_timestamp ? moment(profileFilterDetails.from_timestamp) : moment('1997-02-01')
    )
    const [endDate, setEndDate] = useState(
        profileFilterDetails.to_timestamp ? moment(profileFilterDetails.to_timestamp) : moment()
    )
    const [selectedRow, setSelectedRow] = useState<any>(null)
    const [anchorEl, setAnchorEl] = useState(null)
    const [flagOpenProfileDeleteDialog, setFlagOpenProfileDeleteDialog] = useState(false)
    const [flagAssignAgentPopup, setFlagAssignAgentPopup] = useState(false)

    const dateRangeValue = `${startDate.format('YYYY/MM/DD')} - ${endDate.format('YYYY/MM/DD')}`
    const openMenu = Boolean(anchorEl)

    // Get the appropriate profile data based on type
    const profileData = profileType === 'iul' ? iulProfileDetails : annuityProfileDetails
    const setProfileData = profileType === 'iul' ? setIulProfileDetails : setAnnuityProfileDetails

    useEffect(() => {
        const fetchUserProfileDetails = async () => {
            try {
                const { agent_id, date_filter_flag } = profileFilterDetails
                await getProfileDetails(agent_id, date_filter_flag)
            } catch (error) {
                console.error("Error fetching profile details:", error)
            }
        }

        fetchUserProfileDetails()
    }, [profileType])

    // Update filter when agent changes from parent
    useEffect(() => {
        setProfileFilterDetails((prev: any) => ({
            ...prev,
            agent_id: selectedAgent ? selectedAgent.user_id : 0
        }))
    }, [selectedAgent])

    const handleOpenProfileMenu = (e: any) => {
        setAnchorEl(e?.currentTarget)
    }

    const handleCloseProfileMenu = () => {
        setAnchorEl(null)
    }

    const handleSearch = async () => {
        await getProfileDetails(
            profileFilterDetails.agent_id || 0,
            profileFilterDetails.date_filter_flag,
            profileFilterDetails.from_timestamp,
            profileFilterDetails.to_timestamp
        )
    }

    const handleApply = async (event: any, picker: any) => {
        setStartDate(picker.startDate)
        setEndDate(picker.endDate)
        setProfileFilterDetails((prev: any) => ({
            ...prev,
            from_timestamp: picker.startDate.format('YYYY/MM/DD'),
            to_timestamp: picker.endDate.format('YYYY/MM/DD'),
            date_filter_flag: true
        }))
    }

    const getProfileDetails = async (
        agent_id: number,
        date_filter_flag: boolean,
        from_timestamp?: string | null,
        to_timestamp?: string | null
    ) => {
        setFlagLoader(true)
        const response = await agent.getUserDetailsAPI.get(agent_id, date_filter_flag, from_timestamp, to_timestamp)

        if (response.status) {
            if (response?.data) {
                if (profileType === 'iul') {
                    setProfileData(response?.data?.filter((user: any) => user.user_profile_type_id === 1))
                } else if (profileType === 'annuity') {
                    setProfileData(response?.data?.filter((user: any) => user.user_profile_type_id !== 1))
                } else {
                    setProfileData(response?.data)
                }
            }
            setFlagLoader(false)
        } else {
            setProfileData([])
            setFlagLoader(false)
        }
    }

    const handleProfileRowClick = (params: any) => {
        if (params?.row?.user_profile_type_id === 2) {
            navigate(`/profile/annuityProfileDetails/annuityRoth?user_id=${params?.row?.user_profile_id}`)
        } else if (params?.row?.user_profile_type_id === 3) {
            navigate(`/profile/annuityProfileDetails/annuityIncome?user_id=${params?.row?.user_profile_id}`)
        } else if (params?.row?.user_profile_type_id === 4) {
            navigate(`/profile/annuityProfileDetails/annuityAccumulation?user_id=${params?.row?.user_profile_id}&tab=1`)
        } else if (params?.row?.user_profile_type_id === 1) {
            navigate(`/profile/iulProfileDetails?user_id=${params?.row?.user_profile_id}&tab=1`)
        }
    }

    const handleDeleteProfile = async () => {
        try {
            setFlagLoader(true)
            const response = await deleteUserProfileAPI(selectedRow?.user_profile_id)
            if (response?.status) {
                setFlagOpenProfileDeleteDialog(false)
                await getProfileDetails(
                    profileFilterDetails.agent_id || 0,
                    profileFilterDetails.date_filter_flag,
                    profileFilterDetails.from_timestamp,
                    profileFilterDetails.to_timestamp
                )
                setFlagLoader(false)
                showSnackbar("Profile Deleted Successfully!", "success")
            } else {
                setFlagOpenProfileDeleteDialog(false)
                setFlagLoader(false)
                showSnackbar(response?.error?.message || "Something went wrong!", "error")
            }
        } catch (error) {
            console.log(error, "Something went wrong!")
            setFlagLoader(false)
        } finally {
            setFlagLoader(false)
        }
    }

    const handleAssignToAgent = async (assignmentData: any) => {
        try {
            setFlagLoader(true)
            const payload = {
                ...assignmentData,
            }
            const response = await assignAgentAPI(payload)
            if (response?.status) {
                setFlagLoader(false)
                setFlagAssignAgentPopup(false)
                showSnackbar("Agent assigned successfully!", "success")
                await getProfileDetails(
                    profileFilterDetails.agent_id || 0,
                    profileFilterDetails.date_filter_flag,
                    profileFilterDetails.from_timestamp,
                    profileFilterDetails.to_timestamp
                )
            } else {
                setFlagLoader(false)
                setFlagAssignAgentPopup(false)
                showSnackbar("Something went wrong!", "error")
            }
        } catch (error) {
            console.log('error making api call', error)
        }
    }

    const handleEditBtnClick = (row?: any) => {
        const r = row ?? selectedRow;
        if (r?.user_profile_type_id === 1) {
            navigate(`/profile/newProfile/iul?user_id=${r?.user_profile_id}`);
        } else if (r?.user_profile_type_id === 2) {
            navigate(`/profile/newProfile/annuityRothConversion?user_id=${r?.user_profile_id}`);
        } else if (r?.user_profile_type_id === 3) {
            navigate(`/profile/newProfile/annuityIncome?user_id=${r?.user_profile_id}`);
        } else if (r?.user_profile_type_id === 4) {
            navigate(`/profile/newProfile/annuityAccumulation?user_id=${r?.user_profile_id}`);
        }
    };

    const getProfileColumns = (): ProfileColumn[] => {
        const baseColumns: ProfileColumn[] = [
            {
                field: "user_profile_name",
                headerName: "NAME",
                type: "string",
                align: "left",
                headerAlign: "left",
                width: 200,
            },
            {
                field: "user_profile_created_at",
                headerName: "CREATED DATE",
                valueGetter: (params: GridValueGetterParams) => params?.row?.user_profile_created_at ? moment(params?.row?.user_profile_created_at)?.format("MM/DD/YYYY hh:mm A") : "",
                align: 'left',
                headerAlign: 'left',
                // flex: 1,
                width: 250,
                type: 'dateTime'
            },
            {
                field: "user_email_address",
                headerName: "EMAIL",
                type: "string",
                align: "left",
                headerAlign: "left",
                width: 300,
                renderCell: (params: any) => {
                    const row = params.row
                    return <span>{isSampleProfileRow(row) ? "" : row.user_email_address}</span>
                },
            },
        ]

        if (profileType === "annuity" || profileType === "all" || profileType === "iul") {
            baseColumns.push({
                field: "user_profile_type",
                headerName: "TYPE",
                type: "string",
                align: "left",
                headerAlign: "left",
                width: 300,
                renderCell: (params: any) => {
                    const row = params.row
                    return <>{getProfileTypeLabel(row.user_profile_type_id)}</>
                },
            })
        }

        baseColumns.push(
            {
                field: "user_contact",
                headerName: "CONTACT",
                type: "string",
                align: "left",
                headerAlign: "left",
                width: 180,
                renderCell: (params: any) => {
                    const row = params.row
                    return <span>{isSampleProfileRow(row) ? "" : row.user_contact}</span>
                },
            },
            {
                field: "user_assigned_agent_name",
                headerName: "ASSIGNEE",
                type: "string",
                align: "left",
                headerAlign: "left",
                width: 225,
                renderCell: (params: any) => {
                    const row = params.row
                    return row?.sample_profile ? (
                        <span
                            style={{
                                color: "#008236",
                                backgroundColor: "#DCFCE7",
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 500,
                            }}
                        >
                            Sample Profile
                        </span>
                    ) : (
                        <span>{row?.user_assigned_agent_name}</span>
                    )
                },
            },
            {
                field: "actions",
                headerName: "",
                type: "actions",
                align: "left",
                headerAlign: "left",
                sortable: false,
                disableColumnMenu: true,
                width: 90,
                renderCell: (params: any) => {
                    const row = params.row
                    const menuItemSx = {
                        color: '#000000',
                        '&:hover': {
                            backgroundColor: 'white',
                            color: '#105EA7',
                        },
                    } as const

                    return (
                        <>
                            <Button id="fade-button" aria-haspopup="true">
                                <MoreVertIcon
                                    sx={{
                                        color: "#666666 !important"
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setSelectedRow(row)
                                        handleOpenProfileMenu(e)
                                    }}
                                />
                            </Button>
                            <Menu
                                id="fade-menu"
                                MenuListProps={{ 'aria-labelledby': 'fade-button' }}
                                anchorEl={anchorEl}
                                open={openMenu}
                                onClose={handleCloseProfileMenu}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                PaperProps={{
                                    style: {
                                        boxShadow: "0px 0px 1px 0px #00000033",
                                        background: "#FFFFFF"
                                    },
                                }}
                            >
                                {selectedRow?.sample_profile ? (
                                    <>
                                        <MenuItem
                                            sx={menuItemSx}
                                            onClick={() => {
                                                setAnchorEl(null)
                                                handleEditBtnClick()
                                            }}
                                        >
                                            <SaveIcon fontSize="small" />&nbsp;<span>Save Profile for User</span>
                                        </MenuItem>
                                        {userRole === "admin" && (
                                            <MenuItem
                                                sx={menuItemSx}
                                                onClick={() => handleToggleSampleProfile(false)}
                                            >
                                                <DeleteForeverOutlinedIcon fontSize="small" />&nbsp;<span>Remove as Sample Profile</span>
                                            </MenuItem>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {userRole === "admin" && (
                                            <>
                                                <MenuItem
                                                    sx={menuItemSx}
                                                    onClick={() => {
                                                        setAnchorEl(null)
                                                        setFlagAssignAgentPopup(true)
                                                    }}
                                                >
                                                    <AssignmentIndIcon fontSize="small" />&nbsp;<span>Assign to Agent</span>
                                                </MenuItem>
                                                <MenuItem
                                                    sx={menuItemSx}
                                                    onClick={() => handleToggleSampleProfile(true)}
                                                >
                                                    <SaveIcon fontSize="small" />&nbsp;<span>Set as Sample Profile</span>
                                                </MenuItem>
                                            </>
                                        )}
                                        <MenuItem
                                            sx={menuItemSx}
                                            onClick={() => {
                                                setAnchorEl(null)
                                                handleEditBtnClick(row)
                                            }}
                                        >
                                            <ModeEditOutlineOutlinedIcon fontSize="small" />&nbsp;<span>Edit</span>
                                        </MenuItem>
                                        <MenuItem
                                            sx={menuItemSx}
                                            onClick={() => {
                                                setAnchorEl(null)
                                                setFlagOpenProfileDeleteDialog(true)
                                            }}
                                        >
                                            <DeleteForeverOutlinedIcon fontSize="small" />&nbsp;<span>Delete</span>
                                        </MenuItem>
                                    </>
                                )}
                            </Menu>
                        </>
                    )
                },
            }
        )

        return baseColumns
    }

    const handleToggleSampleProfile = async (flagValue: boolean) => {
        try {
            setFlagLoader(true)
            setAnchorEl(null)
            const response = await agent.setSampleProfileAPI.put({
                user_profile_id: selectedRow?.user_profile_id,
                sample_profile_flag: flagValue,
            })
            if (response?.status) {
                showSnackbar(
                    flagValue ? "Profile marked as sample!" : "Profile removed from samples!",
                    "success"
                )
                await getProfileDetails(
                    profileFilterDetails.agent_id || 0,
                    profileFilterDetails.date_filter_flag,
                    profileFilterDetails.from_timestamp,
                    profileFilterDetails.to_timestamp
                )
            } else {
                showSnackbar(response?.error?.message || "Something went wrong!", "error")
            }
        } catch (error) {
            console.error("Error toggling sample profile flag:", error)
            showSnackbar("Something went wrong!", "error")
        } finally {
            setFlagLoader(false)
        }
    }

    return (
        <>
            {flagOpenProfileDeleteDialog && (
                <GlobalPopUp
                    open={flagOpenProfileDeleteDialog}
                    onClose={() => { setFlagOpenProfileDeleteDialog(false) }}
                    title="Delete Profile"
                    showCloseIcon={true}
                    flagDivider
                    maxWidth={"sm"}
                >
                    <GlobalDeletePopup
                        popupDescription="Are you sure you want to delete this Profile?"
                        handleClose={() => setFlagOpenProfileDeleteDialog(false)}
                        onDelete={() => handleDeleteProfile()}
                    />
                </GlobalPopUp>
            )}
            {flagAssignAgentPopup && (
                <GlobalPopUp
                    open={flagAssignAgentPopup}
                    onClose={() => { setFlagAssignAgentPopup(false) }}
                    title="Assign Profile"
                    showCloseIcon={true}
                    flagDivider
                    maxWidth={"sm"}
                >
                    <AssignProfileToAgentPopup
                        agentOptions={agentOptions}
                        selectedProfileData={selectedRow}
                        handleAssignToAgent={handleAssignToAgent}
                        setFlagAssignAgentPopup={setFlagAssignAgentPopup}
                    />
                </GlobalPopUp>
            )}
            <Grid item xs={12}>
                <Grid container>
                    <Grid item xs={12} className={`${profileStyle.profileTableDatepickerWrapper} `}>
                        <Grid container>
                            <Grid item xs={12} className={`${profileStyle.profileDatepickerItem} `}>
                                <Grid container sx={{ gap: "15px", alignItems: "center" }}>
                                    <Grid item sx={{ cursor: 'pointer' }}>
                                        <DateRangePicker
                                            initialSettings={{
                                                startDate: startDate.toDate(),
                                                endDate: endDate.toDate(),
                                                minDate: new Date(1997, 1, 1),
                                                maxDate: new Date(),
                                                locale: {
                                                    format: 'YYYY/MM/DD',
                                                },
                                            }}
                                            onApply={handleApply}
                                        >
                                            <FormControl sx={{ cursor: 'pointer' }}>
                                                <TextField
                                                    id="textfield-date-range"
                                                    variant={"outlined"}
                                                    className={profileStyle.profileDatepicker}
                                                    size="small"
                                                    value={dateRangeValue}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start" sx={{ cursor: 'pointer' }}>
                                                                <img src={datepicker} alt="" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            border: "1px solid #E2E8F0",
                                                        },
                                                        "& .MuiInputBase-input": {
                                                            cursor: 'pointer'
                                                        },
                                                    }}
                                                />
                                            </FormControl>
                                        </DateRangePicker>
                                    </Grid>

                                    {userDetail?.user_data?.user_role_type.toLowerCase() !== 'agent' && (
                                        <Grid item className={profileStyle.agentDropdownItem}>
                                            <FormControl size='medium' fullWidth sx={{ height: '100%' }}>
                                                <Autocomplete
                                                    fullWidth
                                                    className={profileStyle.agentDropdown}
                                                    disablePortal={false}
                                                    id="combo-box-demo"
                                                    onChange={handleAgentChange}
                                                    value={selectedAgent}
                                                    getOptionLabel={(option: any) => `${option.user_firstname} ${option.user_lastname} `}
                                                    options={[{ user_id: 0, user_firstname: "All Agents", user_lastname: "" }, ...agentOptions]}
                                                    renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder='Select Agent' />}
                                                    sx={{
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            border: "1px solid #E2E8F0",
                                                        },
                                                    }}
                                                />
                                            </FormControl>
                                        </Grid>
                                    )}

                                    <Grid item className={`${profileStyle.profileSearchBtnItem} `}>
                                        <GlobalButton
                                            fullWidth={true}
                                            value='Search'
                                            variant='contained'
                                            className={globalStyle.globalButtonStyleV1}
                                            onClick={() => handleSearch()}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} className={profileStyle.marginClass}>
                                <DataGrid
                                    className={profileStyle.profileTable}
                                    rows={profileData}
                                    columns={getProfileColumns() as GridColDef[]}
                                    getRowId={(userData: any) => userData.user_profile_id}
                                    density="compact"
                                    hideFooterSelectedRowCount
                                    pageSize={10}
                                    rowHeight={60}
                                    components={{
                                        Pagination: CustomPagination,
                                        Toolbar: ProfileListCustomToolbar,
                                        NoRowsOverlay: ProfileListNoRowsOverlay,
                                    }}
                                    autoHeight
                                    onRowClick={handleProfileRowClick}
                                    sx={{
                                        borderRadius: '5px',
                                        overflow: 'hidden',
                                        "& .MuiDataGrid-columnHeaders": {
                                            backgroundColor: "#F2F6F9",
                                            color: '#45556C',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            border: 'none',
                                            minHeight: "54px !important"
                                        },
                                        "& .MuiDataGrid-columnHeaderTitleContainer": {
                                            justifyContent: "start !important"
                                        },
                                        "& .MuiDataGrid-columnHeader": {
                                            padding: '0 16px',
                                            outline: 'none !important',
                                            justifyContent: 'flex-start',
                                        },
                                        "& .MuiDataGrid-columnHeaderTitle": {
                                            fontWeight: 600,
                                            textAlign: 'left',
                                        },
                                        "& .MuiDataGrid-main": {
                                            backgroundColor: "#ffffff",
                                            borderRadius: "5px 5px 0px 0px",
                                            borderTop: "1px solid #E2E8F0",
                                            borderLeft: "1px solid #E2E8F0",
                                            borderRight: "1px solid #E2E8F0",
                                            borderBottom: "none",
                                        },
                                        "& .MuiDataGrid-row": {
                                            backgroundColor: "#ffffff",
                                            borderBottom: '1px solid #E2E8F0 !important',
                                            '&:hover': {
                                                backgroundColor: '#f3f4f6 !important',
                                            },
                                        },
                                        "& .MuiDataGrid-cell": {
                                            borderBottom: 'none',
                                            padding: '0 16px',
                                            fontSize: '14px !important',
                                            outline: 'none !important',
                                        },
                                        "& .MuiDataGrid-cell:focus": {
                                            outline: 'none !important',
                                        },
                                        "& .MuiDataGrid-cell:focus-within": {
                                            outline: 'none !important',
                                        },
                                        "& .MuiDataGrid-cell:hover": {
                                            cursor: "pointer",
                                        },
                                        "& .MuiDataGrid-columnSeparator": {
                                            display: 'none',
                                        },
                                        "& .MuiDataGrid-footerContainer": {
                                            borderTop: "none",
                                            borderLeft: "1px solid #E2E8F0",
                                            borderRight: "1px solid #E2E8F0",
                                            borderBottom: "1px solid #E2E8F0",
                                            backgroundColor: '#F2F6F9',
                                            minHeight: '52px',
                                            padding: '0 16px',
                                            borderRadius: "0px 0px 5px 5px"
                                        },
                                        "& .MuiTablePagination-root": {
                                            color: '#6b7280',
                                        },
                                        "& .MuiTablePagination-actions": {
                                            color: '#6b7280',
                                        },
                                        "& .MuiTablePagination-actions .MuiIconButton-root": {
                                            color: "#6b7280",
                                        },
                                        "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                                            color: "#d1d5db",
                                        },
                                        "& .MuiTablePagination-displayedRows": {
                                            color: '#6b7280',
                                            fontSize: '14px',
                                        },
                                        "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
                                            color: "#525252 !important"
                                        },
                                        "& .MuiDataGrid-toolbarContainer": {
                                            margin: "5px !important"
                                        },
                                        "& .MuiDataGrid-overlay": {
                                            backgroundColor: "#ffffff",
                                        },
                                        "& .MuiDataGrid-columnHeader--moving": {
                                            backgroundColor: "transparent",
                                        },
                                        "& .MuiDataGrid-virtualScrollerContent": {
                                            marginTop: "18px"
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default ProfileList